import { ATTRIBUTE_TYPE_FIELD_VALIDATIONS, CUSTOM_ATTR_GENERIC_REQUIRED_MESSAGE, GENERIC_REQUIRED_MESSAGE, TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs } from "@constants/self-service-constants";
import { AttributeClassificationTypes } from "@enums/attribute-classification-types";
import { CustomAttributeTypeFieldTemplateTypes } from "@iddataweb/idw-common-ui/enums";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { PrefilledAttributeValidation } from "@idw-types/common/prefilled-attribute-validation";
import { PrefilledAttributeTypeFieldValidationErrors, PrefilledAttributeValidationErrors } from "@idw-types/common/prefilled-attribute-validation-errors";
import { AxnAttributeTypeField } from "@idw-types/data/reference-data/axn";
import { FormattedRpServiceConfigurationAttributeWithFields } from "@idw-types/data/rp-service-configuration";
import { safelyParseIntFromString } from "@utils/string-util";
import Alert from "react-bootstrap/Alert";

const pattern = (value: string, pattern: RegExp) => {
    return pattern.test(value);
};

const createErrorAlert = (content: React.ReactNode) => {
    return (
        <Alert variant={"danger"}>
            {content}
        </Alert>
    );
};

export const validateYear = (value: number) => {
    const currentYear = new Date().getFullYear();
    return (value && (value > currentYear || value < 1));
}

export const addMaxLength = (attributeFieldId: string) => {
    if (ATTRIBUTE_TYPE_FIELD_VALIDATIONS[attributeFieldId]) {
        const validations = ATTRIBUTE_TYPE_FIELD_VALIDATIONS[attributeFieldId];
        if (Object.keys(validations).includes('maxLength'))
            return { maxLength: validations.maxLength }
    }

    return {};
}

const isPatternValid = (value: string, regex?: RegExp): boolean => {
    const isPatternValid = pattern(value, regex);
    const isEmpty = value === '';

    if (pattern) {
        if (!isEmpty && !isPatternValid) {
            return false;
        }
    }

    return true;
}

const getAttributeTypeFieldValidationErrors = (
    attributeTypeField: AxnAttributeTypeField,
    fieldValues: { [key: string]: string },
    requiredForSelfServiceVerification: string,
    attributeClassificationName: AttributeClassificationTypes
): Record<string, PrefilledAttributeTypeFieldValidationErrors> => {

    let errors: PrefilledAttributeTypeFieldValidationErrors = {};

    const fieldValue = fieldValues[attributeTypeField.id];
    if (attributeTypeField && ATTRIBUTE_TYPE_FIELD_VALIDATIONS[attributeTypeField.id]) {// for normal attributes
        const validation: PrefilledAttributeValidation = ATTRIBUTE_TYPE_FIELD_VALIDATIONS[attributeTypeField.id];

        if (isStringTrue(requiredForSelfServiceVerification) && validation.isRequired && fieldValue === "") {
            let requiredMessage = GENERIC_REQUIRED_MESSAGE.replace('{{param}}', attributeTypeField.name);

            if (validation?.requiredErrorMessage) {
                requiredMessage = validation.requiredErrorMessage(attributeTypeField.name);
            }

            errors.required = createErrorAlert(<div>{requiredMessage}</div>);
        }

        if (validation.pattern && fieldValue !== "") {
            if (!isPatternValid(fieldValue, validation.pattern)) {
                const patternErrorMessage = validation.patternErrorMessage(attributeTypeField.name);
                errors.pattern = createErrorAlert(<div>{patternErrorMessage || ''}</div>)
            }
        }

        if (validation.customValidator) {
            const isInvalid = validation.customValidator(safelyParseIntFromString(fieldValue));
            if (isInvalid) {
                errors.customValidator = createErrorAlert(<div>{validation?.customValidatorErrorMessage || ''}</div>);
            }
        }

        if (validation.phoneValidator && fieldValue) {
            const dialCode = fieldValues[TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs.dialCode];
            const fullNumber = `+${dialCode}${fieldValue}`;
            const isInvalid = validation.phoneValidator(fullNumber);
            if (isInvalid) {
                errors.phoneValidator = createErrorAlert(<div>{validation?.phoneValidatorErrorMessage || ''}</div>);
            }
        }
    } else if (attributeClassificationName === AttributeClassificationTypes.CUSTOM) {// for smart attribute (custom attributes)
        errors = getCustomAttributeTypeFieldValidationErrors(
            fieldValue, requiredForSelfServiceVerification, attributeTypeField.name, attributeTypeField.templateName
        );
    }

    return Object.keys(errors).length > 0 ? { [attributeTypeField.id]: errors } : {};
}

export const getAllAttributeTypeFieldsValidationErrors = (
    attributeTypeToFieldValueMap: { [attributeTypeId: string]: { [AttributeTypeField: string]: string } },
    attributeTypesWithFields: Array<FormattedRpServiceConfigurationAttributeWithFields>
): PrefilledAttributeValidationErrors => {
    const errors: PrefilledAttributeValidationErrors = {};

    Object.keys(attributeTypeToFieldValueMap).forEach(attributeTypeId => {
        const fieldValues = attributeTypeToFieldValueMap[attributeTypeId];
        const attributeType = attributeTypesWithFields.find(attrType => attrType.attributeType.id === attributeTypeId);

        if (attributeType) {
            if (!errors[attributeTypeId]) {
                errors[attributeTypeId] = {};
            }

            Object.keys(fieldValues).forEach(attributeTypeFieldId => {
                const attributeTypeField = attributeType.fields.find(field => field.id === attributeTypeFieldId);

                errors[attributeTypeId] = {
                    ...errors[attributeTypeId],
                    ...getAttributeTypeFieldValidationErrors(
                        attributeTypeField,
                        fieldValues,
                        attributeType.requiredForSelfServiceVerification,
                        attributeType.attributeClassification.name
                    )
                };
            });
        }
    });

    return errors;
}

const getCustomAttributeTypeFieldValidationErrors = (
    fieldValue: string,
    requiredForSelfServiceVerification: string,
    attributeTypeFieldName: string,
    attributeTypeFieldTemplateName: CustomAttributeTypeFieldTemplateTypes
): PrefilledAttributeTypeFieldValidationErrors => {
    const customAttributeValidationErrors: PrefilledAttributeTypeFieldValidationErrors = {};

    if (isStringTrue(requiredForSelfServiceVerification) && fieldValue === "") {
        const requiredMessage = CUSTOM_ATTR_GENERIC_REQUIRED_MESSAGE.replace('{{param}}', attributeTypeFieldName);
        customAttributeValidationErrors.required = createErrorAlert(<div>{requiredMessage}</div>);
    }

    if (attributeTypeFieldTemplateName === CustomAttributeTypeFieldTemplateTypes.CUSTOM_NUMERIC) {
        if (fieldValue && !pattern(fieldValue, /^\d+$/)) {
            customAttributeValidationErrors.pattern = createErrorAlert(<div>{"Please enter a valid value (Only numbers are allowed, without spaces)."}</div>);
        }
    }

    return customAttributeValidationErrors;
}